import { settlementApis } from "@payping/api";
import { CuToman, ExclamationCircleOutlined, PhRIdentificationCard, PhRPhone, Phone, Profile } from "@payping/icons";

import { setCashOutRequestList } from "@payping/redux/actions";
import { commaMaskedInputHelper, isAlphabet } from "@payping/utils/string";
import {
  Badge,
  Bank,
  Button,
  Card,
  Checkbox,
  Col,
  Flex,
  Form,
  Grid,
  Input,
  Modal,
  Row,
  Space,
  TextArea,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { FormInstance, FormListFieldData } from "@payping/web-components/components/Form/types";
import React, { Key } from "react";

import { ValidMobileNumber, ValidNationalId, ValidSheba } from "@payping/utils/validation";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { SettlementRowType } from "./index";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

interface Props {
  fields: FormListFieldData[];
  form: FormInstance<SettlementRowType>;
  loading: boolean;
}

const List: React.FC<Props> = (props: Props) => {
  const { fields, form, loading } = props;
  const { t } = useAppTranslation();
  const [modal, contextHolder] = Modal.useModal();
  const { createdRequest, overview } = useAppSelector((state) => state.cashOuts);
  const dispatch = useAppDispatch();
  const { lg } = Grid.useBreakpoint();
  const editCard = (key: number) => {
    const rowValues = form.getFieldValue("settlement")[key];

    const settlementList = form.getFieldValue("settlement");
    settlementList[key].isEditing = true;
    settlementList[key].changeIban = rowValues.iban;

    settlementList[key].changeNationalId = rowValues.nationalId;
    settlementList[key].changeMobile = rowValues.mobile;

    settlementList[key].changeAmount = rowValues.amount;
    settlementList[key].changeDescription = rowValues.description;
    form.setFieldValue("settlement", settlementList);
  };

  const editCancel = (key: Key) => {
    form.setFieldValue(
      "settlement",
      form
        .getFieldValue("settlement")
        ?.map?.((settlement: SettlementRowType, index: number) =>
          index === key ? { ...settlement, isEditing: false } : settlement,
        ),
    );
  };

  const shabaValidation = (value: string) => {
    if (value.includes("IR")) return !ValidSheba(value);
    return !ValidSheba(`IR${value}`);
  };

  const [{ loading: loadingSubmit }, saveEdit] = useMessagedAsyncFn(
    async (key) => {
      await form.validateFields();
      const rowValues = form.getFieldValue("settlement")[key];
      const settlementList = form.getFieldValue("settlement");
      settlementList[key].iban = rowValues.changeIban;
      settlementList[key].mobile = rowValues.changeMobile;
      settlementList[key].nationalId = rowValues.changeNationalId;

      settlementList[key].amount = rowValues.changeAmount;
      settlementList[key].description = rowValues.changeDescription;
      let filteredErrors = createdRequest?.errors;
      const correctIRiban = settlementList[key].iban.includes("IR")
        ? settlementList[key].iban
        : `IR${settlementList[key].iban}`;
      if (createdRequest?.code && settlementList[key].amount) {
        if (createdRequest?.errors && createdRequest?.errors?.length >= 0) {
          const errorRow = createdRequest?.errors?.filter((value) => value.code === settlementList[key].code);
          if (errorRow?.length < 1) {
            await settlementApis.updateSettlementWithRequestCodeWithModelPut(createdRequest.code, {
              amount: parseInt(commaMaskedInputHelper.unMask(settlementList[key].amount), 10),
              clientRefId: settlementList[key].clientRefId,
              code: settlementList[key].code,
              description: settlementList[key].description as string,
              iban: correctIRiban,
              mobile: settlementList[key].mobile,
              nationalId: settlementList[key].nationalId,
            });
          } else {
            await settlementApis.createSettlementWithRequestCodeWithModelPost(createdRequest.code, {
              amount: parseInt(commaMaskedInputHelper.unMask(settlementList[key].amount), 10),
              description: settlementList[key].description as string,
              iban: correctIRiban,
              mobile: settlementList[key].mobile,
              nationalId: settlementList[key].nationalId,
            });
            filteredErrors = createdRequest?.errors?.filter((value) => value.code !== settlementList[key].code);
          }
        } else {
          await settlementApis.updateSettlementWithRequestCodeWithModelPut(createdRequest.code, {
            amount: parseInt(commaMaskedInputHelper.unMask(settlementList[key].amount), 10),
            clientRefId: settlementList[key].clientRefId,
            code: settlementList[key].code,
            description: settlementList[key].description as string,
            iban: correctIRiban,
            mobile: settlementList[key].mobile,
            nationalId: settlementList[key].nationalId,
          });
        }
        const response = await settlementApis.settlementsListWithRequestCodeWithQueryWithGetAllGet(
          createdRequest.code,
          undefined,
          undefined,
          undefined,
          undefined,
          true,
        );
        await dispatch(
          setCashOutRequestList({ ...createdRequest, errors: filteredErrors, settlements: response.data }),
        );
      }
    },
    [dispatch, createdRequest?.code, createdRequest?.errors],
    undefined,
    { useProgress: true },
  );

  const [, handleDelete] = useMessagedAsyncFn(
    async (record: SettlementRowType) => {
      modal.confirm({
        cancelText: t("cancel"),
        content: t("message.remove", { subject: t("withdraw.withdraw") }),
        maskClosable: true,
        okText: t("ok"),
        onOk: async () => {
          if (createdRequest && createdRequest?.code && !record.error) {
            await settlementApis.deleteSettlementWithRequestCodeWithSettlementCodeDelete(
              createdRequest?.code,
              record?.code || "",
            );
            const response = await settlementApis.settlementsListWithRequestCodeWithQueryWithGetAllGet(
              createdRequest?.code,
              undefined,
              undefined,
              undefined,
              undefined,
              true,
            );
            dispatch(setCashOutRequestList({ ...createdRequest, settlements: response.data }));
          } else if (createdRequest?.errors) {
            const filteredErrors = createdRequest?.errors.filter((value) => value.code !== record?.code);
            dispatch(setCashOutRequestList({ ...createdRequest, errors: filteredErrors }));
          }
        },
      });
    },
    [dispatch, createdRequest],
    undefined,
    { useProgress: true },
  );

  return (
    <>
      <Space direction={"vertical"} className={"tw-w-full"}>
        {fields.map(({ key, name, ...restField }) => (
          <Card.Ant loading={loading} key={key}>
            <Form.Item hidden valuePropName={"checked"} {...restField} name={[name, "isEditing"]}>
              <Checkbox />
            </Form.Item>
            <Form.Item hidden {...restField} name={[name, "clientRefId"]}>
              <Input />
            </Form.Item>
            <Form.Item hidden {...restField} name={[name, "code"]}>
              <Input />
            </Form.Item>
            <Form.Item
              className={"!tw-mb-0"}
              shouldUpdate={(prev, next) => prev.settlement?.[key]?.isEditing !== next.settlement?.[key]?.isEditing}
            >
              {({ getFieldValue }) => {
                const rowValues = getFieldValue("settlement")[key];

                return rowValues?.isEditing ? (
                  <Row gutter={[8, 16]}>
                    <Col xs={24} lg={7}>
                      <Flex gap={lg ? "8px" : "16px"} vertical>
                        <Form.Item
                          className={"!tw-mb-0"}
                          {...restField}
                          name={[name, "changeIban"]}
                          required={true}
                          rules={[
                            { required: true },
                            {
                              validator(_, value) {
                                if (value && shabaValidation(value)) {
                                  return Promise.reject(new Error(t("settlement.error.shabaValidation")));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input
                            placeholder={t("settlement.create.shebaInput")}
                            addonAfter={"IR"}
                            onlyNumbers
                            canStartWithZero
                            onPressEnter={(event) => {
                              event.preventDefault();
                            }}
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "changeNationalId"]}
                          className={"!tw-mb-0"}
                          rules={[
                            {
                              required: overview?.isCritical,
                            },
                            () => ({
                              validator(_, value) {
                                if (value && !ValidNationalId(value)) {
                                  return Promise.reject(new Error(t("error.nationalId")));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <Input
                            addonAfter={<Profile width={"16px"} />}
                            onlyNumbers
                            canStartWithZero
                            placeholder={t("nationalId")}
                          />
                        </Form.Item>
                      </Flex>
                    </Col>
                    <Col xs={24} lg={7}>
                      <Flex gap={lg ? "8px" : "16px"} vertical>
                        <Form.Item
                          className={"!tw-mb-0"}
                          {...restField}
                          name={[name, "changeAmount"]}
                          required={true}
                          rules={[{ required: true }]}
                        >
                          <Input.Number
                            className={"tw-w-full"}
                            step={1000}
                            separateThousandth
                            min={0}
                            addonAfter={<CuToman width={24} height={24} />}
                            placeholder={t("settlement.create.amountInput")}
                            onPressEnter={(event) => {
                              event.preventDefault();
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, "changeMobile"]}
                          className={"!tw-mb-0"}
                          rules={[
                            {
                              required: overview?.isCritical,
                            },
                            () => ({
                              validator(_, value) {
                                if (value && !ValidMobileNumber(value)) {
                                  return Promise.reject(new Error(t("settlement.id.validPhoneNumber")));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <Input
                            addonAfter={<Phone width={"16px"} />}
                            maxLength={11}
                            onlyNumbers
                            canStartWithZero
                            placeholder={t("mobile")}
                          />
                        </Form.Item>
                      </Flex>
                    </Col>
                    <Col xs={24} lg={7}>
                      <Form.Item
                        className={"!tw-mb-0"}
                        {...restField}
                        name={[name, "changeDescription"]}
                        rules={[
                          {
                            validator(_, value) {
                              if (!isAlphabet(value, { withNumber: true }))
                                return Promise.reject(new Error(t("settlement.error.descriptionException")));
                              if (value && value.length > 30)
                                return Promise.reject(new Error(t("settlement.error.description")));
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <TextArea placeholder={t("settlement.create.descriptionInput")} className="tw-leading-8" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={3}>
                      <Row gutter={[8, 8]}>
                        <Col xs={24} sm={18} lg={24}>
                          {lg ? (
                            <Button block onClick={() => editCancel(key)}>
                              {t("cancel")}
                            </Button>
                          ) : (
                            <Button block type={"primary"} onClick={() => saveEdit(key)} loading={loadingSubmit}>
                              {t("add.add")}
                            </Button>
                          )}
                        </Col>
                        <Col xs={24} sm={6} lg={24}>
                          {lg ? (
                            <Button block type={"primary"} onClick={() => saveEdit(key)} loading={loadingSubmit}>
                              {t("add.add")}
                            </Button>
                          ) : (
                            <Button block onClick={() => editCancel(key)}>
                              {t("cancel")}
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                ) : (
                  <Row className={"tw-align-center tw-flex tw-w-full tw-justify-between"} gutter={[8, 8]}>
                    <Col xl={18} lg={24} xs={24}>
                      <Row>
                        {rowValues?.fullName === " " ? (
                          <Col xs={24}>
                            <Flex gap={"small"}>
                              <Badge status={"error"} />
                              <Typography.Title level={5}>{t("settlement.create.unclearInquiry")}</Typography.Title>
                            </Flex>
                          </Col>
                        ) : (
                          <Col xs={24}>
                            <Typography.Title level={5}>{`${rowValues?.fullName}`}</Typography.Title>
                          </Col>
                        )}
                        <Col xs={24}>
                          <Row>
                            {rowValues?.iban ? (
                              <Col md={10} sm={24} xs={24}>
                                <Space>
                                  <Typography.Text type={"secondary"}>{rowValues?.iban}</Typography.Text>
                                  {rowValues?.iban && <Bank.Icon showTooltip iban={rowValues?.iban} />}
                                </Space>
                              </Col>
                            ) : null}

                            {rowValues?.mobile ? (
                              <Col md={6} sm={12} xs={12}>
                                <Space>
                                  <Typography.Text type={"secondary"}>{rowValues?.mobile}</Typography.Text>
                                  {rowValues?.mobile && <PhRPhone width={"20px"} className="color--gray700" />}
                                </Space>
                              </Col>
                            ) : null}

                            {rowValues?.nationalId ? (
                              <Col md={6} sm={12} xs={12}>
                                <Space>
                                  <Typography.Text type={"secondary"}>{rowValues.nationalId}</Typography.Text>
                                  <PhRIdentificationCard width={"20px"} className="color--gray700" />
                                </Space>
                              </Col>
                            ) : null}
                          </Row>
                        </Col>

                        {rowValues?.description ? (
                          <Col xs={24}>
                            <Typography.Text type={"secondary"}>{rowValues?.description}</Typography.Text>
                          </Col>
                        ) : null}

                        {rowValues?.error ? (
                          <Col xs={24}>
                            <Space>
                              <ExclamationCircleOutlined
                                width={14}
                                className={"color--danger tw-align-center tw-flex"}
                              />
                              <Typography.Text>{rowValues?.error}</Typography.Text>
                            </Space>
                          </Col>
                        ) : null}
                      </Row>
                    </Col>
                    <Col xl={6} lg={24} xs={24} className="tw-align-center tw-flex">
                      <Flex justify="space-between" align="center" className="tw-w-full">
                        <Flex align="center">
                          <Typography.Text>{`${formatLocalizedNumber({ value: Number(rowValues?.amount) })}`}</Typography.Text>
                          <CuToman width={24} height={24} />
                        </Flex>
                        <div>
                          <Button type={"link"} onClick={() => handleDelete(rowValues)}>
                            {t("common.remove")}
                          </Button>
                          <Button type={"link"} onClick={() => editCard(key)}>
                            {t("common.edit")}
                          </Button>
                        </div>
                      </Flex>
                    </Col>
                  </Row>
                );
              }}
            </Form.Item>
          </Card.Ant>
        ))}
      </Space>
      {contextHolder}
    </>
  );
};

export default List;
